import React, { useState } from "react";
import { Check, Mail, RotateCcw, Search, X } from "lucide-react";
import { api } from "./api";
import { BoutonSombre, Eyebrow } from "./ui";

/* Un parrainage douteux : soit on l'attribue à quelqu'un (le bon partira sur SA prochaine
   facture), soit on l'écarte — et dans ce cas on dit pourquoi, pour l'historique. */
export function DecisionParrainage({ item, onFait }) {
  const cible = item.inscription_id || item.ref;
  const nomEcrit = item.texte || (item.detail || "").match(/«\s*(.+?)\s*»/)?.[1] || "";
  const [mode, setMode] = useState("");
  const [q, setQ] = useState(nomEcrit);
  const [resultats, setResultats] = useState([]);
  const [motif, setMotif] = useState("");
  const [erreur, setErreur] = useState("");

  const chercher = async (terme = q) => {
    setErreur("");
    try {
      const r = await api(`/eleves?q=${encodeURIComponent(terme)}&limite=8`);
      setResultats(r.resultats || []);
    } catch (e) { setErreur(e.message); }
  };

  const agir = async (chemin, corps) => {
    setErreur("");
    try {
      await api(`/parrainages/${encodeURIComponent(cible)}/${chemin}`,
        { method: "POST", body: JSON.stringify(corps) });
      setMode("");
      await onFait();
    } catch (e) { setErreur(e.message); }
  };

  if (!mode) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {item.parrain_id && (
          <>
            <BoutonSombre testid="parrainage-suggestion"
              onClick={() => agir("attribuer", { parrain_id: item.parrain_id,
                motif: item.motif_suggestion
                  ? `proposition acceptée — ${item.motif_suggestion}`
                  : `plafond dépassé — accordé quand même à ${item.parrain_suggere}` })}>
              <Check size={14} /> Créditer {item.parrain_suggere}
            </BoutonSombre>
            {item.motif_suggestion && (
              <span className="text-xs text-[var(--muted)]" data-testid="parrainage-motif-suggestion">
                proposition : {item.motif_suggestion}
              </span>
            )}
          </>
        )}
        <button className="btn-ghost" onClick={() => { setMode("attribuer"); chercher(nomEcrit); }} data-testid="parrainage-attribuer">
          <Check size={14} /> Attribuer à un élève
        </button>
        <button className="btn-ghost" onClick={() => setMode("ignorer")} data-testid="parrainage-ignorer">
          <X size={14} /> Écarter
        </button>
      </div>
    );
  }

  return (
    <div className="w-full mt-2 rounded-xl p-3" style={{ background: "#FBFCFD", border: "1px solid var(--border)" }}>
      {erreur && <p className="text-xs mb-2" style={{ color: "#B91C1C" }}>{erreur}</p>}
      {mode === "attribuer" ? (
        <>
          <Eyebrow>Qui doit recevoir le bon de 50 CHF ?</Eyebrow>
          <div className="flex gap-2 mt-2">
            <input className="input-field" style={{ paddingTop: 7, paddingBottom: 7 }} value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && chercher()}
              placeholder="Nom de l'élève ou du responsable…" data-testid="parrainage-recherche" />
            <BoutonSombre onClick={() => chercher()} testid="parrainage-chercher"><Search size={14} /> Chercher</BoutonSombre>
            <button className="text-xs text-[var(--muted)]" onClick={() => setMode("")}>Annuler</button>
          </div>
          <ul className="mt-2 divide-y" style={{ borderColor: "var(--border-soft)" }}>
            {resultats.map((e) => (
              <li key={e.id} className="flex items-center gap-3 py-1.5 text-xs">
                <span className="font-medium">{e.prenom} {e.nom}</span>
                <span className="text-[var(--muted)]">{e.email_famille}</span>
                <button className="btn-ghost ml-auto" data-testid={`parrainage-choisir-${e.id}`}
                  onClick={() => agir("attribuer", { parrain_id: e.id, motif: `attribué à la main depuis « ${nomEcrit} »` })}>
                  Créditer cet élève
                </button>
              </li>
            ))}
            {resultats.length === 0 && <li className="py-2 text-xs text-[var(--muted)]">Aucun résultat.</li>}
          </ul>
        </>
      ) : (
        <>
          <Eyebrow>Pourquoi ce parrainage est-il écarté ?</Eyebrow>
          <div className="flex gap-2 mt-2">
            <input className="input-field" style={{ paddingTop: 7, paddingBottom: 7 }} value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="ex. : le parrain n'est plus membre de l'école" data-testid="parrainage-motif" />
            <BoutonSombre onClick={() => agir("ignorer", { motif })} testid="parrainage-confirmer-ignore"
              disabled={motif.trim().length < 3}>
              Écarter
            </BoutonSombre>
            <button className="text-xs text-[var(--muted)]" onClick={() => setMode("")}>Annuler</button>
          </div>
          <p className="text-[11px] text-[var(--muted)] mt-2">
            La raison reste dans l'historique du parrainage : on saura toujours pourquoi il n'a pas été déduit.
          </p>
        </>
      )}
    </div>
  );
}

/* Les décisions déjà prises, avec le droit de changer d'avis. */
export function DecisionsPrises({ decisions, onFait }) {
  if (!decisions?.length) return null;
  return (
    <details className="text-xs" data-testid="parrainages-decisions">
      <summary className="cursor-pointer" style={{ color: "var(--primary)" }}>
        Décisions déjà prises ({decisions.length}) — on peut revenir en arrière
      </summary>
      <ul className="mt-2 space-y-1">
        {decisions.map((d) => (
          <li key={d.id} className="flex items-center gap-3">
            <span>{d.filleul || d.id}</span>
            <span className="text-[var(--muted)]">
              {d.statut === "attribue" ? `attribué à ${d.parrain}` : "écarté"}
              {d.motif_decision ? ` — ${d.motif_decision}` : ""}
            </span>
            <button className="btn-ghost ml-auto" data-testid={`parrainage-rouvrir-${d.id}`}
              onClick={async () => {
                await api(`/parrainages/${encodeURIComponent(d.id)}/rouvrir`, { method: "POST" });
                await onFait();
              }}>
              <RotateCcw size={12} /> Rouvrir
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}

/* Numéro AVS : le plus souvent l'école l'a sous les yeux — autant le saisir ici. */
/* Le numéro AVS suisse : 13 chiffres commençant par 756. On le met en forme tout seul
   (756.1234.5678.90) et on refuse clairement ce qui n'en est pas un. */
export const avsValide = (v) => /^756\d{10}$/.test(String(v || "").replace(/\D/g, ""));
export const avsFormate = (v) => {
  const n = String(v || "").replace(/\D/g, "").slice(0, 13);
  return [n.slice(0, 3), n.slice(3, 7), n.slice(7, 11), n.slice(11, 13)].filter(Boolean).join(".");
};

/* Un numéro transmis depuis une adresse que le site n'a pas reconnue : l'école choisit
   l'élève à qui il appartient, et le numéro est écrit sur sa fiche. Sans ça, « Vu » rangeait
   la ligne et le numéro transmis était perdu. */
export function AttributionAvs({ item, onFait }) {
  const candidats = item.candidats || [];
  const [cible, setCible] = useState(
    (candidats.find((c) => !c.deja) || candidats[0] || {}).id || "");
  const [erreur, setErreur] = useState("");
  const [ok, setOk] = useState(false);
  const [q, setQ] = useState(item.titre || "");
  const [resultats, setResultats] = useState(null);

  const chercher = async () => {
    setErreur("");
    try {
      const r = await api(`/eleves?q=${encodeURIComponent(q)}&limite=8`);
      setResultats(r.resultats || []);
    } catch (e) { setErreur(e.message); }
  };

  const attribuer = async (eleveId) => {
    setErreur("");
    if (!avsValide(item.avs)) { setErreur("le numéro transmis n'est pas un n° AVS valable"); return; }
    try {
      await api(`/eleves/${encodeURIComponent(eleveId)}`, {
        method: "PATCH",
        body: JSON.stringify({ champs: { avs: avsFormate(item.avs) } }),
      });
      await api("/notifications/avs_a_attribuer/traite-lot", {
        method: "POST", body: JSON.stringify({ refs: [item.ref] }),
      });
      setOk(true);
      await onFait();
    } catch (e) { setErreur(e.message); }
  };

  if (ok) {
    return (
      <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#15803D" }}
        data-testid={`avs-attribue-${item.ref}`}>
        <Check size={14} /> Numéro enregistré
      </span>
    );
  }
  if (!candidats.length) {
    return (
      <span className="flex items-center gap-2 flex-wrap" data-testid={`avs-recherche-${item.ref}`}>
        <input className="input-field" style={{ width: 200, paddingTop: 6, paddingBottom: 6, fontSize: 12 }}
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && chercher()}
          placeholder="Chercher l'élève…" data-testid={`avs-chercher-champ-${item.ref}`} />
        <button className="btn-ghost" onClick={chercher} data-testid={`avs-chercher-${item.ref}`}>
          <Search size={14} /> Chercher
        </button>
        {(resultats || []).map((e) => (
          <button key={e.id} className="btn-ghost" onClick={() => attribuer(e.id)}
            data-testid={`avs-choisir-${e.id}`}>
            <Check size={14} /> {e.prenom} {e.nom}
          </button>
        ))}
        {resultats && resultats.length === 0 && (
          <span className="text-[11px] text-[var(--muted)]">aucun élève trouvé</span>
        )}
        {erreur && <span className="text-[11px]" style={{ color: "#B91C1C" }}>{erreur}</span>}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2 flex-wrap">
      <select className="input-field" style={{ paddingTop: 6, paddingBottom: 6, fontSize: 12 }}
        value={cible} onChange={(e) => setCible(e.target.value)}
        data-testid={`avs-choix-eleve-${item.ref}`}>
        {candidats.map((c) => (
          <option key={c.id} value={c.id}>{c.nom}{c.deja ? " (a déjà un numéro)" : ""}</option>
        ))}
      </select>
      <button className="btn-ghost" onClick={() => attribuer(cible)} data-testid={`avs-attribuer-${item.ref}`}>
        <Check size={14} /> Attribuer le numéro
      </button>
      {erreur && <span className="text-[11px]" style={{ color: "#B91C1C" }}>{erreur}</span>}
    </span>
  );
}

export function SaisieAvs({ eleveId, onFait }) {
  const [valeur, setValeur] = useState("");
  const [erreur, setErreur] = useState("");
  const [ok, setOk] = useState(false);
  const chiffres = String(valeur).replace(/\D/g, "");

  const enregistrer = async () => {
    setErreur("");
    if (!avsValide(valeur)) {
      setErreur(chiffres.length !== 13
        ? `13 chiffres attendus — tu en as saisi ${chiffres.length}`
        : "un numéro AVS commence par 756");
      return;
    }
    try {
      await api(`/eleves/${encodeURIComponent(eleveId)}`,
        { method: "PATCH", body: JSON.stringify({ champs: { avs: avsFormate(valeur) } }) });
      setOk(true);
      await onFait();
    } catch (e) { setErreur(e.message); }
  };

  if (ok) {
    return (
      <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#15803D" }}
        data-testid={`avs-confirme-${eleveId}`}>
        <Check size={14} /> N° {avsFormate(valeur)} enregistré
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <input className="input-field" style={{ width: 175, paddingTop: 6, paddingBottom: 6, fontSize: 12 }}
        placeholder="756.0000.0000.00" value={valeur}
        onChange={(e) => { setValeur(avsFormate(e.target.value)); setErreur(""); }}
        onKeyDown={(e) => e.key === "Enter" && enregistrer()}
        data-testid={`avs-saisie-${eleveId}`} />
      <button className="btn-ghost" onClick={enregistrer} data-testid={`avs-enregistrer-${eleveId}`}>
        <Check size={14} /> Enregistrer
      </button>
      {erreur && <span className="text-[11px]" style={{ color: "#B91C1C" }} data-testid={`avs-erreur-${eleveId}`}>{erreur}</span>}
    </span>
  );
}

/* L'aperçu de l'e-mail avant de l'envoyer à 182 familles. */
export function ApercuEmail({ chemin, titre }) {
  const [ouvert, setOuvert] = useState(false);
  const [apercu, setApercu] = useState(null);
  const charger = async () => {
    setOuvert(true);
    if (!apercu) setApercu(await api(chemin));
  };
  return (
    <>
      <button className="btn-ghost" onClick={charger} data-testid="apercu-email">
        <Mail size={14} /> Relire l'e-mail
      </button>
      {ouvert && apercu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(15,27,45,0.6)" }}
          onClick={() => setOuvert(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}
            data-testid="apercu-email-modale">
            <Eyebrow>{titre}</Eyebrow>
            <div className="text-sm font-semibold mt-2">Objet : {apercu.sujet}</div>
            <div className="text-xs text-[var(--muted)] mt-1">
              {apercu.mode_reel
                ? `Envoyé réellement à la famille (${apercu.destinataire_reel})`
                : `MODE TEST : rien ne partira à ${apercu.destinataire_reel || "la famille"}`}
            </div>
            <div className="mt-4 rounded-xl border p-4" style={{ borderColor: "var(--border)" }}
              dangerouslySetInnerHTML={{ __html: apercu.html }} />
            <div className="mt-4"><BoutonSombre onClick={() => setOuvert(false)} testid="apercu-fermer">Fermer</BoutonSombre></div>
          </div>
        </div>
      )}
    </>
  );
}

/* Une désinscription annoncée sur le site : la console a déjà appliqué le règlement.
   L'école confirme d'un clic, ou fait une exception (hors délai, elle renonce parfois au
   semestre suivant, ou libère la place tout de suite). */
export function DecisionDesinscription({ item, onFait }) {
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState("");

  const ajuster = async (semestreDu, placeGardee) => {
    setEnCours(true);
    setMessage("");
    try {
      const r = await api(`/desinscriptions/${encodeURIComponent(item.ref)}/ajuster`, {
        method: "POST",
        body: JSON.stringify({ semestre_suivant_du: semestreDu, place_conservee: placeGardee }),
      });
      setMessage(r.message);
      await onFait();
    } catch (e) {
      setMessage(e.message);
    }
    setEnCours(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`desinscription-decision-${item.ref}`}>
      <button className="btn-ghost" disabled={enCours}
        data-testid="desinscription-semestre"
        onClick={() => ajuster(!item.semestre_suivant_du, item.place_conservee)}>
        {item.semestre_suivant_du
          ? `Semestre ${item.semestre_suivant} : ne pas facturer`
          : `Semestre ${item.semestre_suivant} : facturer quand même`}
      </button>
      <button className="btn-ghost" disabled={enCours}
        data-testid="desinscription-place"
        onClick={() => ajuster(item.semestre_suivant_du, !item.place_conservee)}>
        {item.place_conservee ? "Libérer la place" : "Garder la place réservée"}
      </button>
      {message && (
        <span className="text-xs font-semibold" style={{ color: "#15803D" }}
          data-testid="desinscription-fait">{message}</span>
      )}
    </div>
  );
}
